import { useEffect, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import type {
  NoticeEntry,
  ShopItem,
  SideLabel,
  Snapshot,
} from "../../services/generated/client.js";
import { cn } from "../../lib/utils.js";
import { highestSeq, sideName, trackTitle, unseen } from "../../lib/notices.js";

/**
 * What just happened, said out loud — phase 16d.
 *
 * # The finding
 *
 * **A steal was invisible, even to the victim.** A card left your timeline and
 * nothing told you. More generally, buying a power gave no acknowledgement,
 * which reads as a broken button.
 *
 * # One stream, two renderers, and they are deliberately different
 *
 * | Surface | Gets |
 * |---|---|
 * | Phone | a transient **toast** *and* a scrollback **feed** for the turn |
 * | Display / TV | the **toast only**, large |
 *
 * ⚠️ A dense scrolling log is unreadable at three metres and is exactly the
 * "animation-heavy idle loop" the display rules warn about on an underpowered TV
 * browser. The television says the one thing that just happened, big; the phone
 * is where you catch up on what you missed while looking at your cards.
 */
/** How long an ordinary notice stays up. Finding 4.2. */
const TOAST_MS = 5000;

/**
 * How many are drawn at once.
 *
 * Finding 4.3 asked for stacking, and a stack has to have a lid: a reconnect
 * can deliver a dozen unseen notices in one publication, and a phone that
 * answers by covering its own board with twelve boxes has replaced one bug with
 * a worse one. The newest are kept — the oldest of a burst are the ones a player
 * has least chance of acting on.
 */
const MAX_STACKED = 3;

/** One notice, on screen, with its own life. */
interface Live {
  entry: NoticeEntry;
  /** Waits to be dismissed rather than expiring. See [`robsTheViewer`]. */
  sticky: boolean;
}

export function NoticeToast({
  board,
  roster,
  yourSide = null,
  scale = "near",
}: {
  board: Snapshot;
  roster: readonly SideLabel[];
  /** Which side is the viewer's, so it can say "You". `null` on a display. */
  yourSide?: string | null;
  /** How far away the reader is. */
  scale?: "near" | "room";
}) {
  // The high-water mark, not a diff of two snapshots: that is what survives a
  // reconnect, where a phone may have missed several publications at once.
  const seen = useRef(0);
  const [live, setLive] = useState<Live[]>([]);

  useEffect(() => {
    const fresh = unseen(board.notices, seen.current);
    seen.current = highestSeq(board.notices, seen.current);
    // A turn boundary empties the list; the mark has to come back with it or
    // the next turn's first notice looks like one already seen.
    //
    // 📌 Whatever is still on screen is deliberately **left alone**. An ordinary
    // toast is already expiring on its own clock, and a sticky one is a robbery
    // the victim has not acknowledged yet — clearing that because the turn moved
    // on would take the notice away at exactly the moment finding #17 says it
    // must not be.
    if (board.notices.length === 0) {
      seen.current = 0;
      return;
    }
    if (fresh.length === 0) {
      return;
    }
    setLive((shown) =>
      [
        ...shown,
        ...fresh.map((entry) => ({ entry, sticky: robsTheViewer(entry, yourSide) })),
      ].slice(-MAX_STACKED),
    );
  }, [board.notices, yourSide]);

  if (live.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="notice-stack"
      className="pointer-events-none flex w-full max-w-md flex-col items-center gap-2"
    >
      {live.map(({ entry, sticky }) => (
        <Toast
          // **Keyed by `seq`**, so each toast owns its own timer for its own
          // lifetime. This is the whole fix — see the note on `Toast`.
          key={entry.seq}
          entry={entry}
          sticky={sticky}
          board={board}
          roster={roster}
          yourSide={yourSide}
          scale={scale}
          onDismiss={() => {
            setLive((shown) => shown.filter((it) => it.entry.seq !== entry.seq));
          }}
        />
      ))}
    </div>
  );
}

/**
 * One notice, which dismisses itself.
 *
 * # 🔴 The bug this shape exists to make impossible
 *
 * Finding 4.1: *"the notification hangs there until the next notification comes
 * in"* — and it is very probably the answer to phase 18's **finding #4**,
 * *"challenges are hanging after the round finished"*, which stayed open for
 * five days as *observed, unconfirmed*.
 *
 * The timer used to live in the effect that watched `board.notices`, and that
 * effect re-runs on **every snapshot** — the array is fresh JSON off the socket,
 * so its identity changes several times a second. React runs the previous
 * cleanup before the next body, so each snapshot **cleared the pending
 * timeout**, and the body then hit its own `if (!last) return` early exit
 * without arming a new one. After the first snapshot following a notice, nothing
 * was ever going to dismiss it. It sat there until the next notice replaced it,
 * which is precisely what was reported — and from the couch a notice that will
 * not leave and an engine that is stuck look identical.
 *
 * ⚠️ **So the timer must not depend on the stream.** It belongs to the toast,
 * whose effect has no dependencies and therefore runs once per mount and is
 * cleared once, on unmount. A notice's life is its own.
 *
 * # What is deliberately kept
 *
 * **A robbery does not expire** — phase 18, finding #17. Four seconds was right
 * for a purchase and wrong for losing a card: the one notice about something
 * taken from *you*, without your input, waits to be dismissed.
 */
function Toast({
  entry,
  sticky,
  board,
  roster,
  yourSide,
  scale,
  onDismiss,
}: {
  entry: NoticeEntry;
  sticky: boolean;
  board: Snapshot;
  roster: readonly SideLabel[];
  yourSide: string | null;
  scale: "near" | "room";
  onDismiss: () => void;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (sticky) {
      return;
    }
    const timer = setTimeout(onDismiss, TOAST_MS);
    return () => {
      clearTimeout(timer);
    };
    // Once per toast, and never re-armed: `onDismiss` closes over a `seq` that
    // cannot change for this component, because the component is keyed by it.
    // Depending on it would reintroduce exactly the re-arming this fixes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sticky]);

  return (
    <p
      role="status"
      data-testid="notice-toast"
      data-sticky={sticky ? "true" : undefined}
      onClick={onDismiss}
      className={cn(
        // `pointer-events-auto` on the toast rather than the stack, so the gaps
        // between toasts stay clickable board.
        "pointer-events-auto w-full rounded-xl border-2 px-4 py-3 text-center font-medium shadow-lg backdrop-blur",
        // **Weight, not only position** (finding #17). Losing a card and
        // confirming a thumbs-up were drawn as the same shape on the page, and
        // shape is what the eye sorts by before it reads anything.
        sticky
          ? "border-wrong-500 bg-wrong-500/25"
          : "border-flash-500/50 bg-flash-500/15",
        // Finding 4.4: the old sizes were `text-sm` for everything a player did
        // not do themselves, on the screen they are holding at arm's length.
        scale === "room" ? "text-3xl" : "text-base",
      )}
    >
      <NoticeLine entry={entry} board={board} roster={roster} yourSide={yourSide} />
      {sticky && (
        <span className="mt-1 block text-xs font-normal opacity-70">
          {t("notice.dismiss")}
        </span>
      )}
    </p>
  );
}

/**
 * Whether this notice is a card being taken **from the person reading it**.
 *
 * The one event in the game that happens to you, matters, and that you did
 * nothing to cause. Everything else in the feed is either your own action or
 * somebody else's business.
 */
function robsTheViewer(entry: NoticeEntry, yourSide: string | null): boolean {
  return (
    yourSide !== null && entry.notice.kind === "stole" && entry.notice.from === yourSide
  );
}

/**
 * The whole turn, oldest first. **Phone only** — see the note above.
 */
export function NoticeFeed({
  board,
  roster,
  yourSide = null,
}: {
  board: Snapshot;
  roster: readonly SideLabel[];
  yourSide?: string | null;
}) {
  const { t } = useTranslation();
  if (board.notices.length === 0) {
    return null;
  }
  return (
    <section aria-label={t("notice.feed")} data-testid="notice-feed">
      <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-white/40">
        {t("notice.feed")}
      </h2>
      <ol className="flex flex-col gap-1">
        {board.notices.map((entry) => (
          <li key={entry.seq} className="text-xs text-white/60">
            <NoticeLine entry={entry} board={board} roster={roster} yourSide={yourSide} />
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * One notice as a sentence, with the sides named.
 *
 * A component rather than a helper taking `t`, because the i18n keys are
 * strongly typed (`locales/shape.ts`) and a function that accepts a `string` key
 * throws that away — the typed `t` is what makes a renamed key a compile error
 * rather than a blank line on somebody's phone.
 */
function NoticeLine({
  entry,
  board,
  roster,
  yourSide,
}: {
  entry: NoticeEntry;
  board: Snapshot;
  roster: readonly SideLabel[];
  yourSide: string | null;
}) {
  const { t } = useTranslation();
  const you = t("notice.you");
  const notice = entry.notice;

  // **The notices that are about the party rather than about a side** — phase
  // 21, ADR 0030. Handled first because everything below this point names an
  // actor, and these have none: a stalled party is the first thing in this feed
  // that happened *to* the room instead of being done *by* somebody in it.
  // Splitting here rather than adding four `side`-less arms to the switch is
  // what keeps `who` and `actorIsYou` non-nullable for the eight that do.
  switch (notice.kind) {
    case "party_stalled":
      return <>{t("notice.partyStalled")}</>;
    case "stall_vote_opened":
      return <>{t("notice.stallVoteOpened")}</>;
    case "stall_vote_kept_waiting":
      return <>{t("notice.stallVoteKeptWaiting")}</>;
    case "party_resumed":
      return <>{t("notice.partyResumed")}</>;
    default:
      break;
  }

  const who = sideName(roster, notice.side, yourSide, you);
  // **Person is a different key, not a pronoun substituted into a template.**
  // Every one of these sentences was written in the third person and handed
  // "Tú" as `{{who}}`, which produced "Tú no se opone" in Spanish and "You
  // says" in English — broken in three of five there too, just less visibly,
  // because English second and third person coincide in the past tense. The
  // `_you` keys are the second person written out; `i18n.test.ts` holds every
  // locale to having one wherever `{{who}}` appears.
  const actorIsYou = yourSide !== null && notice.side === yourSide;

  switch (notice.kind) {
    case "stole": {
      const from = sideName(roster, notice.from, yourSide, you);
      // A steal has **two** people in it, so first person is needed on both
      // slots: "Azu le quitó Chandelier a Tú" was the second casualty of the
      // same bug. Nobody steals from themselves, so the two are exclusive.
      const targetIsYou = yourSide !== null && notice.from === yourSide;
      // Named if the board still shows the card, generic otherwise — the card is
      // resolved from the timeline it now sits on rather than duplicated on the
      // wire, so a blind timeline simply falls back to the sentence the victim
      // actually needed.
      const title = trackTitle(board, notice.track);
      if (title) {
        if (actorIsYou) return <>{t("notice.stoleNamed_you", { from, title })}</>;
        if (targetIsYou) return <>{t("notice.stoleNamed_fromYou", { who, title })}</>;
        return <>{t("notice.stoleNamed", { who, from, title })}</>;
      }
      if (actorIsYou) return <>{t("notice.stole_you", { from })}</>;
      if (targetIsYou) return <>{t("notice.stole_fromYou", { who })}</>;
      return <>{t("notice.stole", { who, from })}</>;
    }
    case "bought": {
      const what = shopItemName(notice.item, t);
      return actorIsYou ? (
        <>{t("notice.bought_you", { what })}</>
      ) : (
        <>{t("notice.bought", { who, what })}</>
      );
    }
    case "challenged":
      return actorIsYou ? (
        <>{t("notice.challenged_you")}</>
      ) : (
        <>{t("notice.challenged", { who })}</>
      );
    case "passed":
      return actorIsYou ? (
        <>{t("notice.passed_you")}</>
      ) : (
        <>{t("notice.passed", { who })}</>
      );
    case "narrowed":
      return actorIsYou ? (
        <>{t("notice.narrowed_you")}</>
      ) : (
        <>{t("notice.narrowed", { who })}</>
      );
  }
}

/** A shop item's display name, spelled out so the key stays statically checked. */
function shopItemName(
  item: ShopItem,
  // ⚠️ `TFunction`, not `ReturnType<typeof useTranslation>["t"]`. The latter
  // carries the whole generated key union through every call, and the tree is
  // large enough now that TypeScript gives up with "type instantiation is
  // excessively deep" — in *this* file, while the key that tipped it was added
  // somewhere else entirely. Keys stay statically checked either way.
  t: TFunction,
): string {
  switch (item) {
    case "challenge":
      return t("shop.items.challenge.name");
    case "steal":
      return t("shop.items.steal.name");
    case "skip":
      return t("shop.items.skip.name");
    case "narrow_options":
      return t("shop.items.narrow_options.name");
    case "blind_timeline":
      return t("shop.items.blind_timeline.name");
    case "insurance":
      return t("shop.items.insurance.name");
  }
}
